import {
  useState, useMemo 
} from 'react';
import {
  Search, X, Package, ExternalLink 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useCatalogItems } from '@/features/catalog/hooks';
import type { CatalogItem } from '@/features/catalog/types';
import { STORAGE_TYPE_LABELS } from '@/features/catalog/types';

interface CatalogItemPickerProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSelect: (item: CatalogItem) => void;
  readonly excludeItemIds?: string[];
}

export function CatalogItemPicker({
  isOpen,
  onClose,
  onSelect,
  excludeItemIds = [],
}: CatalogItemPickerProps) {
  const [search, setSearch] = useState('');
  const {
    data, isLoading 
  } = useCatalogItems({ enabled: isOpen });

  const filteredItems = useMemo(() => {
    if (!data?.data) return [];
    
    return data.data
      .filter((item) => !excludeItemIds.includes(item.itemId))
      .filter((item) => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
          item.name.toLowerCase().includes(searchLower) ||
          item.sku.toLowerCase().includes(searchLower) ||
          item.category.toLowerCase().includes(searchLower)
        );
      });
  }, [data?.data, search, excludeItemIds]);

  if (!isOpen) return null;

  const handleSelect = (item: CatalogItem) => {
    onSelect(item);
    onClose();
    setSearch('');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="w-full max-w-2xl h-[500px] max-h-[80vh] flex flex-col rounded-lg bg-background shadow-xl">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">Select Catalog Item</h3>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/catalog/items"
              target="_blank"
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              View Full Catalog
              <ExternalLink className="h-3 w-3" />
            </Link>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-1.5 hover:bg-muted transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="flex-shrink-0 border-b px-4 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, SKU, or category..."
              className="w-full rounded-md border bg-background pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              autoFocus
            />
          </div>
        </div>

        {/* Items List */}
        <div className="flex-1 overflow-y-auto p-2">
          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground">Loading catalog items...</p>
            </div>
          )}

          {!isLoading && filteredItems.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Package className="h-12 w-12 text-muted-foreground mb-3" />
              <p className="text-muted-foreground">
                {search ? 'No items match your search' : 'No catalog items available'}
              </p>
              <Link to="/catalog/items" className="mt-2">
                <Button variant="outline" size="sm">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Go to Catalog
                </Button>
              </Link>
            </div>
          )}

          {!isLoading && filteredItems.length > 0 && (
            <div className="grid gap-1">
              {filteredItems.map((item) => (
                <button
                  key={item.itemId}
                  type="button"
                  onClick={() => handleSelect(item)}
                  className="flex items-center justify-between rounded-md border p-3 hover:bg-muted/50 transition-colors text-left group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{item.name}</span>
                      <span className="text-xs text-muted-foreground">({item.sku})</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span>{item.category}</span>
                      <span>·</span>
                      <span>{STORAGE_TYPE_LABELS[item.storageType] ?? item.storageType}</span>
                      <span>·</span>
                      <span>{item.unit}</span>
                    </div>
                  </div>
                  <span className="text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                    Select
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between border-t px-4 py-3 text-sm text-muted-foreground">
          <span>{filteredItems.length} items available</span>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
